import { useQuery, useMutation } from '@tanstack/react-query'
import { queryKeys, QUERY_CONFIG } from '../../lib/react-query'
import { queryClient } from '../../lib/react-query/queryClient'
import { paymentsApi } from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import { toast } from 'sonner'
import { useSearchParams } from 'react-router-dom'

// ============================================
// PAYMENT QUERIES
// ============================================

/**
 * Hook for fetching payment history with pagination
 */
export function usePaymentHistory() {
  const orgId = useAuthStore((state) => state.orgId)
  const [searchParams] = useSearchParams()
  const params = searchParams.toString()

  return useQuery({
    queryKey: queryKeys.payments.list(orgId, params),
    queryFn: () => paymentsApi.getHistory(params),
    staleTime: QUERY_CONFIG.LISTS.staleTime,
    enabled: !!orgId,
  })
}

/**
 * Hook for fetching a specific payment detail
 */
export function usePaymentDetail(paymentId: string | null) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.payments.detail(orgId, paymentId || ''),
    queryFn: () => paymentsApi.getPaymentDetail(paymentId!),
    enabled: !!paymentId && !!orgId,
  })
}

// ============================================
// INVOICE QUERIES
// ============================================

/**
 * Hook for fetching invoice list with pagination
 */
export function useInvoices() {
  const orgId = useAuthStore((state) => state.orgId)
  const [searchParams] = useSearchParams()
  const params = searchParams.toString()

  return useQuery({
    queryKey: queryKeys.invoices.list(orgId, params),
    queryFn: () => paymentsApi.getInvoices(params),
    staleTime: QUERY_CONFIG.LISTS.staleTime,
    enabled: !!orgId,
  })
}

/**
 * Hook for fetching a specific invoice detail
 */
export function useInvoiceDetail(invoiceId: string | null) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.invoices.detail(orgId, invoiceId || ''),
    queryFn: () => paymentsApi.getInvoiceDetail(invoiceId!),
    enabled: !!invoiceId && !!orgId,
  })
}

// ============================================
// PAYMENT MUTATIONS
// ============================================

/**
 * Hook for initiating subscription payment
 * Returns payment URL to redirect user
 */
export function usePaySubscription() {
  return useMutation({
    mutationFn: (payload: { payment: string }) =>
      paymentsApi.paySubscription(payload),
    onSuccess: (data) => {
      // Redirect to payment gateway if URL is provided
      if (data.payment_url) {
        window.location.href = data.payment_url
      }
    },
    onError: (error: any) => {

    },
  })
}

/**
 * Hook for verifying a payment/subscription
 */
export function useVerifyPayment() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: ({ authority, status }: { authority: string; status: string }) =>
      paymentsApi.verifySubscription(authority, status),
    onSuccess: () => {
      // Invalidate payment and subscription queries
      queryClient.invalidateQueries({ queryKey: queryKeys.payments.all(orgId) })
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] })
      toast.success('Payment verified successfully')
    },
    onError: (error: any) => {

    },
  })
}
